<template>
  <div class="templates__picker">
    <div class="templates__search">
      <fluent-icon icon="search" class="templates__search-icon" size="24" />
      <input
        ref="search"
        v-model="query"
        type="search"
        :placeholder="$t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
        class="templates__search-input"
      />
    </div>

    <div class="templates__filters">
      <div class="templates__filter-group">
        <span class="templates__filter-label">
          {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.CATEGORY') }}
        </span>
        <div class="templates__chips">
          <button
            v-for="category in categories"
            :key="`category-${category.value}`"
            type="button"
            class="templates__chip"
            :class="{
              'templates__chip--active': templateCategory === category.value,
            }"
            @click.prevent="templateCategory = category.value"
          >
            {{ category.label }}
          </button>
        </div>
      </div>

      <div class="templates__filter-group">
        <span class="templates__filter-label">
          {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.LANGUAGE') }}
        </span>
        <div class="templates__chips">
          <button
            v-for="language in languages"
            :key="`language-${language.value}`"
            type="button"
            class="templates__chip"
            :class="{
              'templates__chip--active': templateLanguage === language.value,
            }"
            @click.prevent="templateLanguage = language.value"
          >
            {{ language.label }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="templateUiFlags.isFetching && !filteredTemplateMessages.length"
      class="text-center"
    >
      <span class="spinner" />
    </div>
    <div v-else-if="showEmptyState" class="templates__empty-state">
      <span class="templates__empty-state-icon material-symbols-outlined">
        assignment
      </span>
      <p class="templates__empty-state-title">{{ emptyState.title }}</p>
      <p class="templates__empty-state-text">{{ emptyState.description }}</p>
      <button
        v-if="emptyState.buttonLabel"
        type="button"
        class="templates__empty-state-button"
        @click.prevent="$emit('create-template')"
      >
        {{ emptyState.buttonLabel }}
      </button>
    </div>
    <div v-else class="templates__list">
      <div
        v-for="template in filteredTemplateMessages"
        :key="templateKey(template)"
        class="templates__card"
        :class="{ 'templates__card--selected': isSelected(template) }"
        @click.prevent="selectTemplate(template)"
      >
        <div class="templates__card-body">
          <p class="templates__card-title">
            <fluent-icon
              v-if="!!getHeaderType(template)"
              :icon="getHeaderType(template)"
              size="16"
              style="vertical-align: bottom"
            />
            {{ template.name }}
          </p>
          <p
            class="templates__card-text"
            dir="auto"
            v-text="getTemplatebody(template)"
          />
          <p
            v-if="getHeaderType(template) && getHeaderType(template) !== 'text'"
            class="templates__card-meta"
          >
            {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.HEADER_TYPE') }}:
            {{ getHeaderTypeText(template) }}
          </p>
        </div>

        <div class="templates__card-aside">
          <div class="templates__tags">
            <span v-if="categoryLabel(template)" class="templates__tag">
              {{ categoryLabel(template) }}
            </span>
            <span v-if="template.language" class="templates__tag">
              {{ languageLabel(template) }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="!filteredTemplateMessages.length" class="templates__empty">
        <template v-if="accountHasNoTemplates">
          {{ $t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_IN_ACCOUNT') }}
        </template>
        <template v-else>
          {{ $t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
          <strong>{{ query }}</strong>
        </template>
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const CATEGORY_SERVICE = 1;
const CATEGORY_MARKETING = 2;

const PLACEHOLDER_TEMPLATE = 'no_templates';

const LANGUAGES = [
  { label: 'ES', codes: ['es'] },
  { label: 'FR', codes: ['fr'] },
  { label: 'HE', codes: ['he'] },
  { label: 'IT', codes: ['it'] },
  { label: 'UA', codes: ['ua', 'uk'] },
  { label: 'EN', codes: ['en'] },
];
const baseLanguage = language =>
  String(language || '')
    .toLowerCase()
    .split(/[_-]/)[0];
const templateKey = template =>
  template ? `${template.name}:${template.language || ''}` : null;

export default {
  props: {
    inboxId: {
      type: Number,
      default: undefined,
    },
    templateFilter: {
      type: Function,
      default: null,
    },
    emptyState: {
      type: Object,
      default: null,
    },
    selectedTemplate: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      query: '',
      templateCategory: null,
      templateLanguage: null,
      localSelectedTemplateKey: templateKey(this.selectedTemplate),
    };
  },
  computed: {
    ...mapGetters({
      templatesList: 'wa_templates/getTemplates',
      templateUiFlags: 'wa_templates/getUIFlags',
    }),
    categories() {
      return [
        {
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.CATEGORY.ALL'),
          value: null,
        },
        {
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.CATEGORY.MARKETING'),
          value: CATEGORY_MARKETING,
        },
        {
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.CATEGORY.SERVICE'),
          value: CATEGORY_SERVICE,
        },
      ];
    },
    whatsAppTemplateMessages() {
      return this.templatesList(this.inboxId).filter(
        template =>
          template.name !== PLACEHOLDER_TEMPLATE &&
          (!this.templateFilter || this.templateFilter(template))
      );
    },
    showEmptyState() {
      return (
        !!this.emptyState &&
        !this.templateUiFlags.isFetching &&
        !this.whatsAppTemplateMessages.length
      );
    },
    accountHasNoTemplates() {
      return this.templatesList(this.inboxId).some(
        ({ name }) => name === PLACEHOLDER_TEMPLATE
      );
    },
    languages() {
      return [
        {
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.CATEGORY.ALL'),
          value: null,
        },
        ...LANGUAGES.map(({ label }) => ({ label, value: label })),
      ];
    },
    filteredTemplateMessages() {
      const query = this.query.toLowerCase();
      let result = this.whatsAppTemplateMessages.filter(
        template =>
          template.name.toLowerCase().includes(query) ||
          template.text.toLowerCase().includes(query)
      );
      if (this.templateCategory !== null) {
        result = result.filter(template =>
          this.matchesCategory(template, this.templateCategory)
        );
      }
      if (this.templateLanguage !== null) {
        const entry = LANGUAGES.find(
          ({ label }) => label === this.templateLanguage
        );
        result = result.filter(({ language }) =>
          entry.codes.includes(baseLanguage(language))
        );
      }
      return result;
    },
  },
  watch: {
    inboxId() {
      this.localSelectedTemplateKey = null;
      this.templateLanguage = null;
      this.refreshTemplateList();
    },
  },
  mounted() {
    this.refreshTemplateList();
  },
  methods: {
    matchesCategory(template, category) {
      return Number(template.category) === category;
    },
    languageLabel(template) {
      return baseLanguage(template.language).toUpperCase();
    },
    categoryLabel(template) {
      if (Number(template.category) === CATEGORY_SERVICE) {
        return this.$t('WHATSAPP_TEMPLATES.PICKER.CATEGORY.SERVICE');
      }
      if (Number(template.category) === CATEGORY_MARKETING) {
        return this.$t('WHATSAPP_TEMPLATES.PICKER.CATEGORY.MARKETING');
      }
      return '';
    },
    templateKey,
    isSelected(template) {
      return this.localSelectedTemplateKey === templateKey(template);
    },
    selectTemplate(template) {
      this.localSelectedTemplateKey = templateKey(template);
      this.$emit('onSelect', template);
    },
    getTemplatebody(template) {
      return template.text;
    },
    getHeaderType(template) {
      switch (parseInt(template.headerType, 10)) {
        case 1:
          return 'text';
        case 3:
          return 'image';
        case 4:
          return 'video';
        case 5:
          return 'document';
        default:
          return null;
      }
    },
    getHeaderTypeText(template) {
      let result = this.getHeaderType(template);
      return result
        ? this.$t(
            `WHATSAPP_TEMPLATES.PICKER.TYPES.${result.toUpperCase()}`
          ).toUpperCase()
        : '---';
    },
    refreshTemplateList() {
      this.$store.dispatch('wa_templates/get', this.inboxId);
    },
  },
};
</script>

<style scoped lang="scss">
.templates__picker {
  @apply flex flex-col gap-4 w-full min-h-0;
}

.templates__search {
  @apply flex items-center gap-2 h-10 py-2 ltr:pl-3 ltr:pr-1 rtl:pr-3 rtl:pl-1
    bg-white dark:bg-slate-900 rounded-lg
    border border-solid border-gray-300 dark:border-slate-700
    shadow-[0px_-1px_2px_0px_rgba(61,72,87,0.10)_inset];

  .templates__search-icon {
    @apply text-gray-500 dark:text-slate-400 shrink-0;
  }

  .templates__search-input {
    @apply bg-transparent border-0 shadow-none h-auto m-0 p-0 text-base
      text-gray-700 dark:text-slate-100 placeholder:text-gray-400;
  }
}

.templates__filters {
  @apply flex flex-wrap items-start gap-6;
}

.templates__filter-group {
  @apply flex items-center gap-3;
}

.templates__filter-label {
  @apply text-lg leading-relaxed text-gray-900 dark:text-slate-100;
}

.templates__chips {
  @apply flex flex-wrap items-center gap-2;
}

.templates__chip {
  @apply px-2.5 py-[0.3125rem] rounded-lg text-sm font-medium leading-6
    bg-[#F6F7F9] text-[#0A2540] cursor-pointer
    dark:bg-slate-700 dark:text-slate-100;

  &--active {
    @apply bg-green-500 text-white dark:bg-green-500 dark:text-white;
  }
}

.templates__list {
  @apply flex flex-col gap-3 overflow-y-auto max-h-[21.375rem] min-h-[10rem];
}

.templates__card {
  @apply flex justify-between items-start gap-3 p-6 cursor-pointer
    bg-white dark:bg-slate-800 rounded-xl
    border border-solid border-neutral-200 dark:border-slate-700
    hover:border-teal-500;

  &--selected {
    @apply border-teal-500 bg-teal-50 dark:bg-teal-900/20;

    .templates__tag {
      @apply border-teal-500 text-teal-700 bg-transparent
        dark:border-teal-500 dark:text-teal-300;
    }
  }
}

.templates__card-body {
  @apply flex flex-col gap-2 flex-1 min-w-0;
}

.templates__card-title {
  @apply m-0 text-base font-semibold leading-6 text-primary dark:text-slate-50
    break-words;
}

.templates__card-text {
  @apply m-0 text-sm leading-5 text-secondary dark:text-slate-300 break-words
    whitespace-pre-wrap;
}

.templates__card-meta {
  @apply m-0 text-xs text-gray-500 dark:text-slate-400;
}

.templates__card-aside {
  @apply flex flex-col justify-center items-end gap-3 shrink-0;
}

.templates__tags {
  @apply flex items-start gap-3;
}

.templates__tag {
  @apply px-2 py-1 rounded-lg text-sm leading-5 whitespace-nowrap
    bg-[rgba(71,85,105,0.04)] text-[#475569]
    border border-solid border-[rgba(0,0,0,0.12)]
    dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600;
}

.templates__empty-state {
  @apply flex flex-col items-center py-12 px-6 text-center;

  &-icon {
    @apply flex items-center justify-center w-12 h-12 rounded-xl
      bg-[#F3F4F6] dark:bg-slate-800 text-[24px] text-[#6B7280] dark:text-slate-400;
  }

  &-title {
    @apply m-0 mt-4 text-base leading-6 font-semibold text-[#1F2937]
      dark:text-slate-100;
  }

  &-text {
    @apply m-0 mt-2 max-w-[26.25rem] text-sm leading-5 text-[#6B7280]
      dark:text-slate-400;
  }

  &-button {
    @apply mt-4 h-10 py-2.5 px-5 rounded-lg text-sm font-semibold text-white
      bg-green-500 hover:bg-green-600;
  }
}

.templates__empty {
  @apply m-0 py-4 text-sm text-gray-500 dark:text-slate-400;
}
</style>
